import React, { useMemo } from 'react'

import { Card } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, object, string, number, oneOfType, oneOf, bool, shape,
} from 'prop-types'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import CollectionItem from './Components/CollectionItem/CollectionItem'
import FormatsSection from './Components/FormatsSection/FormatsSection'
import HeaderSection from './Components/HeaderSection/HeaderSection'
import ProvidedByDesignerSection from './Components/ProvidedByDesignerSection/ProvidedByDesignerSection'
import VerifiedByCGTraderSection from './Components/VerifiedByCGTraderSection/VerifiedByCGTraderSection'
import { getProvidedByDesignerDetails } from './Utils/getProvidedByDesignerDetails'

function DetailsArea({
  collections,
  publishDate = null,
  id,
  details = EMPTY_ARRAY,
  polygons = 0,
  vertices = 0,
  triangles = 0,
  geometryType = '',
  cgtVerificationData = null,
  printModelVerificationData = null,
  nativeFormats = EMPTY_ARRAY,
  exchangeFormats = EMPTY_ARRAY,
  ungroupedFormats = EMPTY_ARRAY,
  units,
  locale = 'en',
  summaryFileExtensions = EMPTY_OBJECT,
  showCgtVerification = false,
  unwrappedUvs = null,
  readyFor3dPrinting = false,
  animated = false,
  rigged = false,
  gameReady = false,
  pbr = false,
  isPreSupported = false,
  isSplitIntoMultipleParts = false,
  multiplePartsDetails = null,
  tagForLinking,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const pageConfig = usePageConfig()
  const is3dModelAndShouldBeShown = !is3dPrintModel &&
    Boolean(cgtVerificationData) &&
    cgtVerificationData.score >= 50
  const is3dPrintModelAndShouldBeShown = is3dPrintModel && printModelVerificationData
  const viewerOverride = typeof pageConfig?.product?.cgtVerificationOverride === 'boolean'
    ? pageConfig.product.cgtVerificationOverride
    : false
  const shouldShowVerifiedSection = (is3dModelAndShouldBeShown || is3dPrintModelAndShouldBeShown) && (showCgtVerification || viewerOverride)
  const data = is3dPrintModel ? printModelVerificationData : cgtVerificationData?.data
  const geometryDetails = cgtVerificationData?.data?.details
  const isCollection = Object.keys(summaryFileExtensions).length > 0
  const providedByDesignerSectionDetails = useMemo(
    () => getProvidedByDesignerDetails(details, summaryFileExtensions, isCollection),
    [details, summaryFileExtensions, isCollection]
  )

  const filteredExchangeFormats = useMemo(() => {
    if (!exchangeFormats?.length || !nativeFormats?.length) {
      return exchangeFormats || []
    }

    const nativeFormatTitles = nativeFormats.map(format => format.title)

    return exchangeFormats.filter(format => !nativeFormatTitles.includes(format.title))
  }, [exchangeFormats, nativeFormats])

  return (
    <Card hasBorder isRounded customClass="details-area">
      <HeaderSection id={id} />
      <div className="details-area__body">
        {nativeFormats?.length > 0 && (
          <FormatsSection
            data={nativeFormats}
            title={`Native file ${nativeFormats.length > 1 ? 'formats' : 'format'}`}
            summaryFileExtensions={summaryFileExtensions}
            showExpanded
          />
        )}
        {filteredExchangeFormats?.length > 0 && (
          <FormatsSection
            data={filteredExchangeFormats}
            title={`Exchange ${filteredExchangeFormats.length > 1 ? 'formats' : 'format'}`}
            summaryFileExtensions={summaryFileExtensions}
            showTooltip={!isCollection}
          />
        )}
        {ungroupedFormats?.length > 0 && (
          <FormatsSection
            data={ungroupedFormats}
            title={`File ${ungroupedFormats.length > 1 ? 'formats' : 'format'}`}
            summaryFileExtensions={summaryFileExtensions}
            showTooltip={!isCollection}
          />
        )}
        {shouldShowVerifiedSection && (
          <VerifiedByCGTraderSection
            data={data}
            geometryDetails={geometryDetails}
            locale={locale}
          />
        )}
        <ProvidedByDesignerSection
          details={providedByDesignerSectionDetails}
          tagForLinking={tagForLinking}
          publishDate={publishDate}
          id={id}
          polygons={polygons}
          vertices={vertices}
          triangles={triangles}
          geometryType={geometryType}
          units={units}
          isCollection={isCollection}
          unwrappedUvs={unwrappedUvs}
          readyFor3dPrinting={readyFor3dPrinting}
          animated={animated}
          rigged={rigged}
          gameReady={gameReady}
          pbr={pbr}
          isPreSupported={isPreSupported}
          isSplitIntoMultipleParts={isSplitIntoMultipleParts}
          multiplePartsDetails={multiplePartsDetails}
        />
      </div>
      {collections.length > 0 && (
        <div className="details-area__footer">
          <span className="details-area__section-title">This model is also available in packs</span>
          <div className="details-area__collections">
            {collections.map((collection) => (
              <CollectionItem key={collection.id} {...collection} />
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

DetailsArea.propTypes = {
  tagForLinking: string,
  collections: arrayOf(shape({
    collectionPrice: number.isRequired,
    id: number.isRequired,
    itemsCount: number.isRequired,
    saleOffDiscount: number.isRequired,
    thumbnail: string.isRequired,
    title: string.isRequired,
    url: string.isRequired,
  })).isRequired,
  publishDate: string,
  id: number.isRequired,
  details: oneOfType([
    shape({
      animated: bool,
      rigged: bool,
      game_ready: bool,
      pbr: bool,
      textures: bool,
      materials: bool,
      uv_mapping: bool,
      plugins_used: bool,
    }),
    arrayOf(shape({
      title: string,
      value: oneOfType([string, number, bool]),
    })),
  ]),
  polygons: number,
  vertices: number,
  triangles: number,
  geometryType: oneOfType([string, object]),
  cgtVerificationData: shape({
    data: shape({
      files: shape({
        fbxFormat: shape({
          pass: bool,
        }),
      }),
      geometry: shape({
        facetedGeometry: shape({
          pass: bool,
        }),
        noNgons: shape({
          pass: bool,
        }),
        nonManifoldVertices: shape({
          pass: bool,
        }),
      }),
      naming: shape({
        allowedCharacters: shape({
          pass: bool,
        }),
      }),
      scene: shape({
        dccObjectTypes: shape({
          pass: bool,
        }),
      }),
      texturesAndMaterials: shape({
        embeddedTextures: shape({
          pass: bool,
        }),
        missingRequiredTextures: shape({
          pass: bool,
        }),
        numberOfMaterialsPerObject: shape({
          pass: bool,
        }),
        textureAspectRatio: shape({
          pass: bool,
        }),
        textureResolutionNotPowerOf2: shape({
          pass: bool,
        }),
      }),
      uV: shape({
        numberOfUVs: shape({
          pass: bool,
        }),
        overlappedUvPerUVIsland: shape({
          pass: bool,
        }),
      }),
    }),
    details: shape({
      animated: bool,
      rigged: bool,
      game_ready: bool,
      pbr: bool,
      textures: bool,
      materials: bool,
      uv_mapping: bool,
      plugins_used: bool,
    }),
    reportType: string,
    score: number,
  }),
  printModelVerificationData: shape({
    cleanGeometry: shape({
      manifoldGeometry: bool.isRequired,
      noSelfIntersections: bool.isRequired,
      noOverlappingFaces: bool.isRequired,
      noZeroAngledCorners: bool.isRequired,
      noZeroLengthEdges: bool.isRequired,
      noZeroAreaFaces: bool.isRequired,
      noBadContiguousEdges: bool.isRequired,
      noOpenEdges: bool.isRequired,
    }),
    score: number.isRequired,
  }),
  nativeFormats: arrayOf(shape({
    extensionPath: string,
    fileSize: string.isRequired,
    title: string.isRequired,
    fullName: string,
    version: string,
    rendererName: string,
    rendererVersion: oneOfType([string, number]),
    itemFileVersions: arrayOf(shape({
      version: oneOfType([string, number]),
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
  })),
  exchangeFormats: arrayOf(shape({
    extensionPath: string,
    fileSize: string.isRequired,
    title: string.isRequired,
    fullName: string,
    version: string,
    rendererName: string,
    rendererVersion: oneOfType([string, number]),
    itemFileVersions: arrayOf(shape({
      version: oneOfType([string, number]),
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
  })),
  ungroupedFormats: arrayOf(shape({
    extensionPath: string,
    fileSize: string.isRequired,
    title: string.isRequired,
    fullName: string,
    version: string,
    rendererName: string,
    rendererVersion: oneOfType([string, number]),
    itemFileVersions: arrayOf(shape({
      version: oneOfType([string, number]),
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
  })),
  units: string,
  locale: string,
  summaryFileExtensions: object,
  showCgtVerification: bool,
  unwrappedUvs: oneOf(['yes', 'no', 'unknown', 'mixed', 'non-overlapping']),
  readyFor3dPrinting: bool,
  animated: bool,
  rigged: bool,
  gameReady: bool,
  pbr: bool,
  isPreSupported: bool,
  isSplitIntoMultipleParts: bool,
  multiplePartsDetails: shape({
    numberOfParts: string,
    unit: string,
    width: string,
    depth: string,
    height: string,
    assemblyInstructions: string,
  }),
}

export default DetailsArea
